import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2Off, Loader2 } from 'lucide-react';

import { BoundObjectView, useDocumentAnalysisAPI } from '../DocumentAnalysisAPI';

/**
 * Worüber dieses Dokument redet.
 *
 * Die Analyse liest aus einem Dokument Objekte heraus und bindet sie an das, was im Bestand schon
 * steht. Bis hierher war das nur technisch zu sehen — Klassenbezeichner und Kennungen in der
 * Verwaltungsansicht. Für die Frage im Datenraum ist das keine Antwort: „Lease" ist kein Wort, das
 * jemand benutzt, und `68f3a1b2c3d4e5f6a7b8c9d0` ist keine Wohnung.
 *
 * Gezeigt wird deshalb, was das Modell selbst an Beschriftungen mitbringt — die Art als Wort, das
 * Objekt über seine identifizierenden Werte. Übersetzt wird auf dem Server; hier wird gezeichnet.
 *
 * Erst ab „analysiert": Vorher gibt es keine Bindungen, und eine leere Überschrift über einem
 * laufenden Vorgang sähe aus wie ein Ergebnis.
 */

export default function BoundObjectsPanel({ analysisId }: { analysisId: string }) {
    const { t } = useTranslation();
    const api = useDocumentAnalysisAPI();

    const [bindings, setBindings] = useState<BoundObjectView[] | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!analysisId) {
            setBindings(null);
            return;
        }

        let cancelled = false;

        setLoading(true);
        setBindings(null);

        api.getBindings(analysisId)
            .then((result) => {
                if (!cancelled) setBindings(result.bindings ?? []);
            })
            .catch((error: any) => {
                // Die Bindungen sind eine Zugabe zur Datei, kein Grund für eine Meldung über die
                // Seite. Wo sie fehlen, steht eben nichts.
                console.error('Could not load the bound objects', error);
                if (!cancelled) setBindings([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // Das API-Objekt wird bei jedem Zeichnen neu gebaut; sein Verhalten ist stabil.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysisId]);

    if (loading && bindings === null) {
        return (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                {t('portfolioDashboard.dataRoom.boundObjectsLoading')}
            </div>
        );
    }

    // Nichts gebunden ist eine Aussage, aber keine, die eine Überschrift verdient: Das Dokument ist
    // ausgewertet, und im Bestand entsprach dem nichts.
    if (!bindings || bindings.length === 0) return <></>;

    return (
        <div className="mt-1 flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">
                {t('portfolioDashboard.dataRoom.boundObjects', { count: bindings.length })}
            </span>

            <ul className="flex flex-col gap-1.5">
                {bindings.map((binding) => (
                    <li
                        key={`${binding.objectId}-${binding.term}`}
                        className="rounded-md bg-muted/50 px-2 py-1.5 text-xs"
                    >
                        <div className="flex flex-wrap items-baseline gap-x-1.5">
                            <span className="font-medium">{binding.classLabel}</span>
                            {binding.designation ? (
                                <span className="min-w-0 break-words">{binding.designation}</span>
                            ) : (
                                <></>
                            )}
                        </div>

                        {/* Wie das Dokument die Sache nennt — daran erkennt man die Bindung wieder.
                            Nur wo es sich von der Benennung unterscheidet: zweimal dasselbe zu
                            lesen ist keine zweite Auskunft. */}
                        {binding.term && binding.term !== binding.designation ? (
                            <div className="text-muted-foreground">
                                {t('portfolioDashboard.dataRoom.asWrittenIn', { term: binding.term })}
                            </div>
                        ) : (
                            <></>
                        )}

                        {/* Ein Verweis ins Leere wird benannt statt verschwiegen: Im Bestand wird
                            gelöscht, ohne auf Analysen Rücksicht zu nehmen, und der Befund von
                            damals bleibt trotzdem wahr. */}
                        {binding.missing ? (
                            <div className="mt-0.5 flex items-center gap-1 text-muted-foreground">
                                <Link2Off className="h-3 w-3 shrink-0" aria-hidden />
                                {t('portfolioDashboard.dataRoom.boundObjectGone')}
                            </div>
                        ) : (
                            <></>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
